import { applyBrand, resolveBrand } from '@/shared/brand';
import { loadRuntimeConfig, type RuntimeConfig } from '@/shared/config';
import { baseUrlsFromConfig, clearGeoLatLongCache, configureHttp, primeGeoLatLong } from '@/shared/http';
import { configureAgentsHttp } from '@/shared/http/agents/runtime';
import { clearFlowDrafts } from '@/shared/flow/flow-draft';
import { flushTelemetry, setTelemetryUser, startTelemetry } from '@/shared/telemetry';

import { initAuthSession, type AuthSession } from './auth-session';

export interface BootOptions {
  /** Correspondente escolhido no seletor da casca (store `shell`, U22). */
  getCurrentTenant: () => string | undefined;
  /** Assina a troca de correspondente, para `useCurrentTenant()` reagir. */
  subscribeCurrentTenant?: (onChange: () => void) => () => void;
  /** Logout: a casca limpa o cache do TanStack Query aqui (o legado não limpava, risco 10). */
  onSignedOut?: () => void;
  /** `X-UY3-Client-Geo`; fica `false` até o Compliance decidir. */
  clientGeo?: boolean;
}

export interface BootResult {
  config: RuntimeConfig;
  session: AuthSession;
}

/**
 * Sobe o runtime antes do primeiro render: config do domínio → sessão Cognito → camada HTTP →
 * telemetria (em idle). A restauração da sessão começa aqui; `requireAuth` espera por ela.
 */
export async function bootRuntime(options: BootOptions): Promise<BootResult> {
  const config = await loadRuntimeConfig();
  // Marca do corban antes do primeiro render: sem flash de acento nem de logo (U16).
  applyBrand(resolveBrand(config), globalThis.location.hostname);
  const clientGeo = options.clientGeo ?? false;

  const session = initAuthSession({
    // Mesmo pool e client do legado (U05); `USE_MFA` truthy, como no callback de login legado.
    pool: { UserPoolId: config.USER_POOL_ID, ClientId: config.USER_CLIENT_ID, useMfa: Boolean(config.USE_MFA) },
    mainTitle: config.MAIN_TITLE,
    onSignedIn: (user) => {
      void setTelemetryUser(user.sub);
      if (clientGeo) void primeGeoLatLong();
    },
    onSignedOut: () => {
      void setTelemetryUser(undefined);
      clearGeoLatLongCache();
      // Rascunho de fluxo é por usuário: não sobrevive ao logout (F5, B1).
      clearFlowDrafts();
      flushTelemetry(true);
      options.onSignedOut?.();
    },
  });

  configureHttp({
    baseUrls: baseUrlsFromConfig(config),
    getAccessToken: () => session.getAccessToken(),
    getCurrentTenant: options.getCurrentTenant,
    ...(options.subscribeCurrentTenant ? { subscribeCurrentTenant: options.subscribeCurrentTenant } : {}),
    clientGeo,
  });
  // A BFF do Agents valida o ID token (tem `aud`), nunca o access token (discovery/12 03 §4).
  configureAgentsHttp({ baseUrl: config.AGENTS_BFF_URL, getIdToken: () => session.getIdToken() });

  const version: unknown = import.meta.env['VITE_APP_VERSION'];
  void startTelemetry({
    collectorUrl: config.OTEL_COLLECTOR_URL,
    serviceVersion: typeof version === 'string' ? version : 'dev',
    environment: import.meta.env.MODE,
    host: globalThis.location.hostname,
  });

  void session.ready();
  return { config, session };
}
